/**
 * F-050 / F-112 — supplier info of a part: which layout a sourcing opens, which boxes are still
 * empty, and the pre-fill from PIM's free-text "Vendor/Supplier" column for Update requests
 * (Amanda: strip "PREVIOUS VERSION RECORD" and everything after it). Pure functions, mirrored
 * one-to-one by `DdPartSupplierPrefill.php`; both run the shared cases in
 * `contracts/dd-part-supplier-prefill-cases.json`.
 */
import {
  findPartSourcing,
  findSupplierLayout,
  type SupplierLayoutKey
} from '@/lib/dd-queue/data/partOperations'

export const PREVIOUS_VERSION_MARKER = 'PREVIOUS VERSION RECORD'
const PREVIOUS_VERSION_MARKER_RE = new RegExp(
  PREVIOUS_VERSION_MARKER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  'i'
)

export interface SupplierPrefill {
  layout: SupplierLayoutKey
  fields: Record<string, string>
}

/** The supplier-info layout a sourcing opens (null = no supplier boxes / unknown sourcing). */
export function supplierLayoutForSourcing(sourcing: string): SupplierLayoutKey | null {
  return findPartSourcing(sourcing)?.supplier_layout ?? null
}

/** Only an in-house manufactured part has a stock to pick; an unknown sourcing keeps the old rule. */
export function sourcingNeedsSubstrate(sourcing: string | null | undefined): boolean {
  if (!sourcing) return true
  return findPartSourcing(sourcing)?.needs_substrate ?? true
}

/**
 * Labels of the layout's required boxes that are still empty. Whitespace-only counts as empty;
 * "N/A" counts as filled (Amanda: "at least take the time to input N/A"). An unknown layout has
 * nothing to require.
 */
export function supplierMissingFields(
  layout: string | null | undefined,
  fields: Record<string, string | null | undefined> | null | undefined
): string[] {
  const def = layout ? findSupplierLayout(layout) : null
  if (!def) return []
  return def.fields
    .filter((f) => f.required && String(fields?.[f.key] ?? '').trim() === '')
    .map((f) => f.label)
}

/** Field keys that decide the Stocked Hardware layout when present in PIM text. */
const STOCKED_KEYS: readonly string[] = [
  'internal_shorthand',
  'uom_purchased',
  'uom_sold',
  'purchase_price',
  'sale_price'
]

/** PIM label (normalised: lowercase, letters / digits / single spaces) → field key. */
const LABEL_ALIASES: Record<string, string> = {
  supplier: 'supplier',
  'supplier name': 'supplier',
  vendor: 'supplier',
  'vendor name': 'supplier',
  'supplier url': 'supplier_url',
  'vendor url': 'supplier_url',
  'supplier link': 'supplier_url',
  url: 'supplier_url',
  link: 'supplier_url',
  'supplier part number': 'supplier_part_number',
  'suplier part number': 'supplier_part_number',
  'supplier part': 'supplier_part_number',
  'supplier part no': 'supplier_part_number',
  'supplier part num': 'supplier_part_number',
  'vendor part number': 'supplier_part_number',
  'part number': 'supplier_part_number',
  'part no': 'supplier_part_number',
  'additional info': 'additional_info',
  'additional information': 'additional_info',
  'additonal info': 'additional_info',
  'additinal info': 'additional_info',
  notes: 'additional_info',
  note: 'additional_info',
  'internal shorthand': 'internal_shorthand',
  shorthand: 'internal_shorthand',
  'unit of measure for units purchased': 'uom_purchased',
  'unit of measure purchased': 'uom_purchased',
  'uom purchased': 'uom_purchased',
  'units purchased': 'uom_purchased',
  'unit of measure for how it is sold': 'uom_sold',
  'unit of measure sold': 'uom_sold',
  'uom sold': 'uom_sold',
  'how it is sold': 'uom_sold',
  'purchase price': 'purchase_price',
  'purchase cost': 'purchase_price',
  cost: 'purchase_price',
  'sale price': 'sale_price',
  'sales price': 'sale_price',
  'sell price': 'sale_price'
}

const LABEL_LINE = /^([A-Za-z][A-Za-z0-9 .#/()&'-]{0,60}?)\s*:\s*(.*)$/
const URL_LINE = /^https?:\/\//i

function normaliseLabel(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function appendValue(current: string | undefined, piece: string, continuation: boolean): string {
  const prev = (current ?? '').trim()
  const next = piece.trim()
  if (next === '') return prev
  if (prev === '') return next
  if (continuation) {
    // A wrapped URL continues without a separator; wrapped prose gets a space.
    return /^https?:\/\//i.test(prev) && !/\s/.test(next) ? prev + next : `${prev} ${next}`
  }
  return `${prev} | ${next}`
}

/**
 * The PIM "Vendor/Supplier" text as supplier boxes. Everything from the marker on is dropped;
 * "Label: value" lines land in their box (typos and wording variants mapped), a line without a
 * label continues the previous box (wrapped URLs re-joined), unknown labels and boxes the layout
 * does not have are kept as "Label: value" in Additional Info. Layout: any stocked-hardware box
 * present → Stocked Hardware, otherwise Drop Shipped Items. Null when nothing is left.
 */
export function parseSupplierFromPim(text: string | null | undefined): SupplierPrefill | null {
  let raw = String(text ?? '')
  // Case-insensitive search on the ORIGINAL string: upper-casing can change the length
  // ("ß" → "SS"), which would shift the cut point.
  const marker = raw.search(PREVIOUS_VERSION_MARKER_RE)
  if (marker >= 0) raw = raw.slice(0, marker)
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l !== '')
  if (lines.length === 0) return null

  const parsed: Record<string, string> = {}
  const extras: string[] = []
  let current: string | null = null

  for (const line of lines) {
    const m = URL_LINE.test(line) ? null : LABEL_LINE.exec(line)
    if (m) {
      const key = LABEL_ALIASES[normaliseLabel(m[1]!)]
      if (key) {
        parsed[key] = appendValue(parsed[key], m[2]!, false)
        current = key
        continue
      }
      // A fact PIM keeps under a label of its own — kept verbatim.
      extras.push(line)
      current = null
      continue
    }
    if (current) {
      parsed[current] = appendValue(parsed[current], line, true)
    } else if (extras.length > 0) {
      // A wrapped line of the previous unlabelled fact.
      extras[extras.length - 1] = appendValue(extras[extras.length - 1], line, true)
    } else {
      extras.push(line)
    }
  }

  const layout: SupplierLayoutKey = STOCKED_KEYS.some((k) => (parsed[k] ?? '') !== '')
    ? 'stocked_hardware'
    : 'drop_shipped'
  const def = findSupplierLayout(layout)!
  const allowed = new Set(def.fields.map((f) => f.key))

  const fields: Record<string, string> = {}
  const spill: string[] = []
  for (const field of def.fields) {
    const v = (parsed[field.key] ?? '').trim()
    if (v !== '') fields[field.key] = v
  }
  for (const [key, value] of Object.entries(parsed)) {
    if (allowed.has(key) || value.trim() === '') continue
    spill.push(`${labelOf(key)}: ${value.trim()}`)
  }
  const extraText = [...spill, ...extras].join(' | ')
  if (extraText !== '') {
    fields.additional_info = fields.additional_info
      ? `${fields.additional_info} | ${extraText}`
      : extraText
  }
  if (Object.keys(fields).length === 0) return null
  return { layout, fields }
}

function labelOf(key: string): string {
  for (const layout of ['stocked_hardware', 'drop_shipped'] as const) {
    const f = findSupplierLayout(layout)?.fields.find((x) => x.key === key)
    if (f) return f.label
  }
  return key
}
